/**
 * Keyboard + touch input. Tracks held state and per-frame "just pressed" edges.
 * Action names mirror the original game's input map.
 */
export type Action =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'jump'
  | 'walk'
  | 'umbrella'
  | 'hologram'
  | 'map'
  | 'show_info'
  | 'pause'
  | 'debug_die';

// Exact yknytt default bindings (GDKnyttKeys.cs): arrows move, S=jump, A=walk,
// D=umbrella, W=hologram, Q=info, M=map, Escape=pause, Enter also = down.
const DEFAULT_BINDINGS: Record<string, Action> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  Enter: 'down',
  KeyS: 'jump',
  KeyA: 'walk',
  KeyD: 'umbrella',
  KeyW: 'hologram',
  KeyQ: 'show_info',
  KeyM: 'map',
  Escape: 'pause',
  F10: 'debug_die', // GDKnyttKeys.cs:45 debug_die0 — the standard un-stick suicide key
};

// yknytt joypad defaults (GDKnyttKeys.cs, non-Unix). Godot 3 button indices
// 0-15 coincide with the W3C Standard Gamepad mapping, so these carry over
// directly: d-pad 12-15, X=jump, B=hologram, Y=info, L2=walk, R2=umbrella,
// L1=pause, R1=map. (A/Joy(0) is unbound upstream too.)
const GAMEPAD_BINDINGS: Record<number, Action> = {
  12: 'up',
  13: 'down',
  14: 'left',
  15: 'right',
  1: 'hologram',
  2: 'jump',
  3: 'show_info',
  4: 'pause',
  5: 'map',
  6: 'walk',
  7: 'umbrella',
};

const STICK_DEADZONE = 0.5;

// Actions driven by the area's automation system (everything except UI controls).
const GAMEPLAY: ReadonlySet<Action> = new Set<Action>([
  'up',
  'down',
  'left',
  'right',
  'jump',
  'walk',
  'umbrella',
  'hologram',
]);

export class InputManager {
  private held = new Set<Action>();
  private pressedEdge = new Set<Action>(); // pressed this frame
  private releasedEdge = new Set<Action>();
  private heldPrev = new Set<Action>();
  enabled = true;

  // External (touch) overrides, merged each poll.
  private touchHeld = new Set<Action>();

  // Automation overlay (Automation/BlockUser objects). Mirrors JuniInput/AltInput:
  //   gameplay input = (!blockInput && real) || (hasAltInput && alt)
  blockInput = false; // a BlockUser object is present → real input ignored
  hasAltInput = false; // an Automation object is present → altInput is OR'd in
  private altHeld = new Set<Action>();
  private altHeldPrev = new Set<Action>();
  private altPressedEdge = new Set<Action>();
  private altReleasedEdge = new Set<Action>();

  constructor(private target: Window = window) {
    target.addEventListener('keydown', this.onKeyDown);
    target.addEventListener('keyup', this.onKeyUp);
    target.addEventListener('blur', this.onBlur);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    const action = DEFAULT_BINDINGS[e.code];
    if (!action) return;
    e.preventDefault();
    if (!this.enabled && action !== 'pause') return;
    this.held.add(action);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    const action = DEFAULT_BINDINGS[e.code];
    if (!action) return;
    e.preventDefault();
    this.held.delete(action);
  };

  private onBlur = (): void => {
    this.held.clear();
    this.touchHeld.clear();
  };

  /**
   * Release every physically-held action (Cutscene.releaseAll / PausePanel.unpause
   * ActionRelease): called when a modal UI (pause/map/cutscene) closes so the key
   * that closed it (still physically down) doesn't fire an edge on the next poll.
   * OS key-repeat re-adds genuinely held movement keys almost immediately.
   */
  releaseAll(): void {
    this.held.clear(); // keyboard only — touchHeld is owned by TouchControls' pointer tracking
  }

  setTouch(action: Action, down: boolean): void {
    if (down) this.touchHeld.add(action);
    else this.touchHeld.delete(action);
  }

  /** Set/clear an automation-driven (altInput) action. */
  setAlt(action: Action, down: boolean): void {
    if (down) this.altHeld.add(action);
    else this.altHeld.delete(action);
  }

  /** Forget all automation state + flags (called on area change). */
  resetAlt(): void {
    this.blockInput = false;
    this.hasAltInput = false;
    this.altHeld.clear();
    this.altHeldPrev.clear();
    this.altPressedEdge.clear();
    this.altReleasedEdge.clear();
  }

  /** Read connected gamepads into a held-action set (polled, no events). */
  private pollGamepads(): Set<Action> {
    const held = new Set<Action>();
    if (typeof navigator === 'undefined' || !navigator.getGamepads) return held;
    for (const pad of navigator.getGamepads()) {
      if (!pad || !pad.connected) continue;
      for (const [index, action] of Object.entries(GAMEPAD_BINDINGS)) {
        if (pad.buttons[Number(index)]?.pressed) held.add(action);
      }
      // Left stick doubles as the d-pad.
      const [x, y] = pad.axes;
      if (x <= -STICK_DEADZONE) held.add('left');
      if (x >= STICK_DEADZONE) held.add('right');
      if (y <= -STICK_DEADZONE) held.add('up');
      if (y >= STICK_DEADZONE) held.add('down');
    }
    if (!this.enabled) {
      // Mirror keyboard behaviour: while disabled only 'pause' registers.
      const pause = held.has('pause');
      held.clear();
      if (pause) held.add('pause');
    }
    return held;
  }

  /** Compute edges for this frame. Call once per frame before reading. */
  poll(): void {
    const now = new Set<Action>([...this.held, ...this.touchHeld, ...this.pollGamepads()]);
    this.pressedEdge.clear();
    this.releasedEdge.clear();
    for (const a of now) if (!this.heldPrev.has(a)) this.pressedEdge.add(a);
    for (const a of this.heldPrev) if (!now.has(a)) this.releasedEdge.add(a);
    this.heldPrev = now;

    // Automation edges (diff of the altInput held set since last frame).
    this.altPressedEdge.clear();
    this.altReleasedEdge.clear();
    for (const a of this.altHeld) if (!this.altHeldPrev.has(a)) this.altPressedEdge.add(a);
    for (const a of this.altHeldPrev) if (!this.altHeld.has(a)) this.altReleasedEdge.add(a);
    this.altHeldPrev = new Set(this.altHeld);
  }

  /**
   * Raw physically-held state (keyboard or touch), live even while the game
   * loop is paused (unlike isHeld, which reflects the last poll()). Used by
   * the on-screen gamepad to light up buttons.
   */
  rawHeld(a: Action): boolean {
    return this.held.has(a) || this.touchHeld.has(a);
  }

  // JuniInput.cs gates reads on Enabled (checkPressed/checkJustPressed return false
  // immediately, even for already-held keys) — not just new keydown events, so a key
  // held across a disable stops driving Juni at once. checkJustReleased is NOT
  // Enabled-gated upstream, and Direction reads altInput before the Enabled check.
  isHeld(a: Action): boolean {
    if (!GAMEPLAY.has(a)) return this.heldPrev.has(a);
    if (!this.enabled) return false;
    return (!this.blockInput && this.heldPrev.has(a)) || (this.hasAltInput && this.altHeldPrev.has(a));
  }
  justPressed(a: Action): boolean {
    if (!GAMEPLAY.has(a)) return this.pressedEdge.has(a);
    if (!this.enabled) return false;
    return (!this.blockInput && this.pressedEdge.has(a)) || (this.hasAltInput && this.altPressedEdge.has(a));
  }
  justReleased(a: Action): boolean {
    if (!GAMEPLAY.has(a)) return this.releasedEdge.has(a);
    return (!this.blockInput && this.releasedEdge.has(a)) || (this.hasAltInput && this.altReleasedEdge.has(a));
  }

  /** Horizontal direction (-1, 0, 1). Automation (altInput) takes priority. */
  get direction(): number {
    if (this.hasAltInput && this.altHeldPrev.has('left')) return -1;
    if (this.hasAltInput && this.altHeldPrev.has('right')) return 1;
    if (!this.enabled || this.blockInput) return 0;
    let d = 0;
    if (this.heldPrev.has('left')) d -= 1;
    if (this.heldPrev.has('right')) d += 1;
    return d;
  }

  destroy(): void {
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('blur', this.onBlur);
  }
}
